import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, openSignedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, Upload, Camera, Image as ImageIcon, Eye, Loader2, CheckCircle2, RotateCcw, BellRing } from "lucide-react";
import { PaymentBadge } from "@/components/OrderPayments";

const MAX = 2 * 1024 * 1024;
const OK_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const OK_EXT = /\.(pdf|jpe?g|png)$/i;
const isMobile = () => window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 768;

// Shrink large camera photos to a JPEG under 2 MB; PDFs and small images pass through unchanged.
export async function prepareDoc(file, label = "invoices") {
  if (!OK_TYPES.includes(file.type) && !OK_EXT.test(file.name)) throw new Error(`Only PDF, JPEG or PNG ${label} are accepted`);
  if (file.size <= MAX) return file;
  if (!file.type.startsWith("image/")) throw new Error("File is too large. Maximum size is 2 MB.");
  const img = await createImageBitmap(file);
  for (const [side, q] of [[2400, 0.85], [2000, 0.8], [1600, 0.75], [1280, 0.7]]) {
    const scale = Math.min(1, side / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", q));
    if (blob && blob.size <= MAX) return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
  }
  throw new Error("File is too large. Maximum size is 2 MB.");
}

function Picker({ inputRef, onPick, accept, capture, testid }) {
  return <input ref={inputRef} type="file" accept={accept} capture={capture} className="hidden" data-testid={testid}
    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onPick(f); }} />;
}

export function InvoiceSection({ order, isSupplier, canUpload, canPayment, onChanged }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const inv = order.invoice;
  const allowed = ["Accepted", "Delivered", "Missed Delivery"].includes(order.status);
  const showUpload = isSupplier && canUpload && allowed;
  if (!inv && !showUpload) return null;

  const upload = async (raw) => {
    setBusy(true);
    try {
      const file = await prepareDoc(raw);
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/orders/${order.id}/invoice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(inv ? "Invoice replaced" : "Invoice uploaded", { description: "The buyer has been notified." });
      onChanged(r.data.invoice);
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Upload failed"); } finally { setBusy(false); }
  };
  const view = () => openSignedFile(inv.file_id).catch((e) => toast.error(e.response?.data?.detail || "Could not open invoice"));
  const remind = async () => {
    setBusy(true);
    try { await api.post(`/orders/${order.id}/payment-reminder`); toast.success("Reminder sent to the buyer"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const setPaid = async (status) => {
    setBusy(true);
    try { await api.post(`/orders/${order.id}/payment`, { status }); toast.success(status === "paid" ? "Marked as paid" : "Marked as payment pending"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const mobile = isMobile();
  const accept = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-[#FAF8F5] p-4" data-testid="order-invoice-section">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-400">Invoice</p>
      {inv ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#C85A32]"><FileText className="h-5 w-5" /></div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-stone-800" data-testid="order-invoice-filename">{inv.filename}</p>
              <p className="text-xs text-stone-500">{inv.size < 1024 ? `${inv.size} B` : `${Math.round(inv.size / 1024)} KB`} · uploaded {new Date(inv.uploaded_at).toLocaleString("en-IN")}{inv.uploaded_by ? ` by ${inv.uploaded_by}` : ""}</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={view} data-testid="order-invoice-view-btn"><Eye className="mr-1 h-4 w-4" /> View / Download</Button>
        </div>
      ) : (
        <p className="text-sm text-stone-500">{showUpload ? "Attach the invoice for this order. The buyer will be notified." : ""}</p>
      )}
      {inv && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[#E5E0D8] pt-3" data-testid="order-payment-section">
          <span className="flex items-center gap-2 text-sm text-stone-600">Payment: <PaymentBadge status={order.payment_status || "pending"} /></span>
          {isSupplier && canPayment && order.payment_status !== "paid" && <Button size="sm" variant="outline" disabled={busy} onClick={remind} data-testid="order-payment-reminder-btn"><BellRing className="mr-1 h-4 w-4" /> Send reminder</Button>}
          {isSupplier && canPayment && (order.payment_status === "paid"
            ? <Button size="sm" variant="ghost" disabled={busy} onClick={() => setPaid("pending")} data-testid="order-mark-pending-btn"><RotateCcw className="mr-1 h-4 w-4" /> Mark as Pending</Button>
            : <Button size="sm" disabled={busy} onClick={() => setPaid("paid")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="order-mark-paid-btn"><CheckCircle2 className="mr-1 h-4 w-4" /> Mark as Paid</Button>)}
        </div>
      )}
      {showUpload && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Picker inputRef={fileRef} onPick={upload} accept={accept} testid="order-invoice-file-input" />
          <Picker inputRef={camRef} onPick={upload} accept="image/jpeg,image/png" capture="environment" testid="order-invoice-camera-input" />
          {mobile ? (
            <>
              <Button size="sm" disabled={busy} onClick={() => fileRef.current?.click()} className="bg-[#0D5C3A] hover:bg-[#084229]" data-testid="order-invoice-gallery-btn">
                {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ImageIcon className="mr-1 h-4 w-4" />} {inv ? "Replace from Gallery" : "Upload from Gallery"}
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => camRef.current?.click()} data-testid="order-invoice-camera-btn"><Camera className="mr-1 h-4 w-4" /> {inv ? "Retake Photo & Replace" : "Take Photo & Upload"}</Button>
            </>
          ) : (
            <Button size="sm" disabled={busy} onClick={() => fileRef.current?.click()} className="bg-[#0D5C3A] hover:bg-[#084229]" data-testid="order-invoice-upload-btn">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} {inv ? "Replace Invoice" : "Upload Invoice"}
            </Button>
          )}
          <p className="w-full text-xs text-stone-400">PDF, JPEG or PNG · max 2 MB{mobile ? " · large photos are compressed automatically" : ""}</p>
        </div>
      )}
    </div>
  );
}
